/**
 * Utility functions for shipping display logic
 */

/**
 * Helper function to calculate discount amount from adjustments
 * @param {Array} adjustments - Array of price adjustments
 * @returns {number} Total discount amount
 */
const calculateDiscountAmount = (adjustments) => {
    return adjustments.reduce((sum, adj) => sum + Math.abs(adj.price || 0), 0)
}

/**
 * Helper function to determine which shipping case applies
 * @param {Object} shippingItem - The shipping item from basket
 * @param {Array} orderAdjustments - Order level adjustments
 * @returns {string} Case type
 */
const determineShippingCase = (shippingItem, orderAdjustments) => {
    if (!shippingItem) return 'NO_SHIPPING'

    const {basePrice, priceAfterItemDiscount, priceAdjustments} = shippingItem
    const hasShippingAdjustments = priceAdjustments?.length > 0
    const hasOrderAdjustments = orderAdjustments?.length > 0

    if (basePrice === 0) return 'FREE_FROM_START'
    if (basePrice > 0 && priceAfterItemDiscount === 0 && hasShippingAdjustments)
        return 'FREE_VIA_PROMOTION'
    if (hasOrderAdjustments) return 'ORDER_DISCOUNTS'
    return 'REGULAR_PAID'
}

/**
 * @param {Object} basket - The basket object containing shipping information
 * @returns {Object} Object containing shipping display information
 */
export const getShippingDisplayInfo = (basket) => {
    const shippingItem = basket?.shippingItems?.[0]
    const orderAdjustments = basket?.orderPriceAdjustments || []

    const caseType = determineShippingCase(shippingItem, orderAdjustments)

    // Base result object to prevent duplication
    const baseResult = {
        showOriginalPrice: true,
        showDiscount: false,
        showFree: false,
        originalPrice: 0,
        discountAmount: 0,
        hasAdjustments: false
    }

    switch (caseType) {
        case 'NO_SHIPPING':
            // No shipping item available
            return {
                ...baseResult,
                showOriginalPrice: false,
                showFree: true
            }

        case 'FREE_FROM_START':
            // Shipping is free from the beginning (basePrice = 0)
            return {
                ...baseResult,
                showOriginalPrice: false,
                showFree: true
            }

        case 'FREE_VIA_PROMOTION':
            // Free shipping via shipping promotion/coupon
            return {
                ...baseResult,
                showDiscount: true,
                originalPrice: shippingItem.basePrice,
                discountAmount: calculateDiscountAmount(shippingItem.priceAdjustments),
                hasAdjustments: true,
                adjustments: shippingItem.priceAdjustments
            }

        case 'ORDER_DISCOUNTS':
            // Order has discounts (coupons/gift cards) but shipping is paid
            return {
                ...baseResult,
                originalPrice: shippingItem.basePrice
            }

        case 'REGULAR_PAID':
        default:
            // Regular paid shipping without any discounts
            return {
                ...baseResult,
                originalPrice: shippingItem.priceAfterItemDiscount || shippingItem.basePrice
            }
    }
}
